import { Box, Button, Card, CardActions, CardContent, CardMedia, Chip, Container, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { createProgram, deleteProgram, getPrograms, updateProgram } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import SkeletonCardWithActions from '../../components/skeleton/SkeletonCardWithActions/SkeletonCardWithActions';
import { FormData, useProgramForm } from './hooks/useProgramForm';
import CreateProgramDrawerContent from './partials/CreateProgramDrawerContent';
import { uploadFile } from '../../api/services/file';
import EditProgramDrawerContent from './partials/EditProgramDrawerContent';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';

const s3BucketName = 'admin-programs-assets';

const FallbackView = ({ programName }: { programName: string }) => (
    <Box
        sx={{
            height: '130px',
            width: '100%',
            borderRadius: '12px',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <BrokenImageIcon sx={{ fontSize: 40, color: 'gray' }} />
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {programName} image unavailable
        </Typography>
    </Box>
);

const ProgramsPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = {
        name: '',
        code: '',
        coverPhoto: '',
        banner: '',
        isUndergrad: false
    };

    // Initial touch fields for error validation
    const initialTouchFields = {
        name: false,
        code: false,
        coverPhoto: false,
        banner: false,
        isUndergrad: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useProgramForm({
        initialFormData,
    });

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [isProgramsLoading, setIsProgramsLoading] = useState<boolean>(false);
    const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>({});

    // Drawable states for create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected program id for edit state
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');
    const [selectedProgramTitle, setSelectedProgramTitle] = useState<string>('');

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Cover photo
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Banner photo
    const [selectedBannerImage, setSelectedBannerImage] = useState<File | null>(null);
    const bannerFileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleImageError = (url: string) => {
        setImageErrorMap((prev) => ({ ...prev, [url]: true }));
    };

    const fetchPrograms = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'create' | 'edit' | string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add New Program' : 'Update Program');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let coverPhotoUrl = formData.coverPhoto;
            let bannerUrl = formData.banner;

            if (fileInputRef.current?.files?.[0]) {
                const file = fileInputRef.current.files[0];
                const uploadResponse = await uploadFile(s3BucketName, file);
                coverPhotoUrl = uploadResponse.data.location;
            }

            if (bannerFileInputRef.current?.files?.[0]) {
                const file = bannerFileInputRef.current.files[0];
                const uploadResponse = await uploadFile(s3BucketName, file);
                bannerUrl = uploadResponse.data.location;
            }

            const payload = {
                ...formData,
                coverPhoto: coverPhotoUrl,
                banner: bannerUrl
            };

            const createResponse = await createProgram(payload);
            setPrograms((prevPrograms) => [createResponse.data, ...prevPrograms]);
            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setCurrentDrawableState('');
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            setSelectedBannerImage(null);
            if (bannerFileInputRef.current) {
                bannerFileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData),
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleEdit = (selectedTerm: Program) => {
        setSelectedProgramId(selectedTerm._id);
        setSelectedProgramTitle(selectedTerm.name);

        const programData: FormData = {
            name: selectedTerm.name,
            code: selectedTerm.code,
            coverPhoto: selectedTerm.coverPhoto,
            banner: selectedTerm.banner,
            isUndergrad: selectedTerm.isUndergrad
        };
        setOriginalFormData(programData);
        resetForm(programData);
        toggleDrawer(true, 'edit')();
    };

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            let coverPhotoUrl = formData.coverPhoto;
            let bannerUrl = formData.banner;

            if (fileInputRef.current?.files?.[0]) {
                const file = fileInputRef.current.files[0];
                const uploadResponse = await uploadFile(s3BucketName, file);
                coverPhotoUrl = uploadResponse.data.location;
            }


            if (bannerFileInputRef.current?.files?.[0]) {
                const file = bannerFileInputRef.current.files[0];
                const uploadResponse = await uploadFile(s3BucketName, file);
                bannerUrl = uploadResponse.data.location;
            }

            const payload = {
                ...formData,
                coverPhoto: coverPhotoUrl,
                banner: bannerUrl
            };
            const updateResponse = await updateProgram(selectedProgramId, payload);
            setPrograms((prevPrograms) =>
                prevPrograms.map((program) =>
                    program._id === updateResponse.data._id ? updateResponse.data : program
                )
            );
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setSelectedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            setSelectedBannerImage(null);
            if (bannerFileInputRef.current) {
                bannerFileInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDelete = (program: Program) => {
        setSelectedProgramId(program._id);
        setSelectedProgramTitle(program.name);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteProgram(selectedProgramId);
            setPrograms(prevPrograms => prevPrograms.filter(program => program._id !== selectedProgramId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedProgramTitle('');
            setSelectedProgramId('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            setRawFormData((prev) => ({
                ...prev,
                coverPhoto: 'selected',
            }));
        }
    };

    const handleUploadBannerClick = () => {
        bannerFileInputRef.current?.click();
    };

    const handleBannerFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedBannerImage(file);
            setRawFormData((prev) => ({
                ...prev,
                banner: 'selected',
            }));
        }
    };

    const handleDrawerClose = () => {
        toggleDrawer(false, currentDrawableState)()
        setRawFormData(initialFormData);
        setFormData(initialFormData);
        setTouchedFields(initialTouchFields);
        setCurrentDrawableState('');
        setIsSubmitting(false);

        // Clear file input and image
        setSelectedImage(null);
        setSelectedBannerImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        if (bannerFileInputRef.current) {
            bannerFileInputRef.current.value = '';
        }
    };

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            setIsProgramsLoading(true);
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }
                setCurrentAdminName(firstName);
                await fetchPrograms();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsProgramsLoading(false);
            } finally {
                setIsProgramsLoading(false);
            }
        };
        run();
    }, [fetchPrograms, navigate]);

    return (
        <main className="main-content">
            <Sidebar />

            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Programs' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: '2'
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'create')}
                        >
                            New Program
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 3 }}>
                            List of Programs
                        </Typography>

                        <Grid container rowSpacing={1} columnSpacing={1}>
                            {isProgramsLoading ? (
                                Array.from({ length: 10 }).map((_, idx) => (
                                    <Grid size={{ xs: 6, md: 2.4, lg: 2.4 }} key={idx}>
                                        <SkeletonCardWithActions />
                                    </Grid>
                                ))
                            ) : programs.length === 0 ? (
                                <Grid size={12}>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        height="200px"
                                        width="100%"
                                    >
                                        <Typography variant="body1" color="text.secondary">
                                            No programs available.
                                        </Typography>
                                    </Box>
                                </Grid>
                            )
                                : (
                                    programs.map((program: Program) => (
                                        <Grid size={{ xs: 6, md: 2.4, lg: 2.4 }} key={program._id}>
                                            <Card sx={{ maxWidth: '220px', borderRadius: '12px' }}>
                                                <Box sx={{ m: 1 }}>
                                                    {imageErrorMap[program.coverPhoto] ? (
                                                        <FallbackView programName={program.name} />
                                                    ) : (
                                                        <CardMedia
                                                            component="img"
                                                            alt={program.name}
                                                            image={program.coverPhoto}
                                                            sx={{
                                                                height: '130px',
                                                                width: '100%',
                                                                borderRadius: '12px'
                                                            }}
                                                            onError={() => handleImageError(program.coverPhoto)}
                                                        />
                                                    )}
                                                    <CardContent sx={{ px: 0, py: '16px' }}>
                                                        <Tooltip title={program.name}>
                                                            <Typography
                                                                gutterBottom
                                                                variant="h6"
                                                                component="div"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    mb: 0,
                                                                    textOverflow: "ellipsis",
                                                                    overflow: "hidden",
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: 200,
                                                                }}
                                                                noWrap
                                                            >
                                                                {program.name}
                                                            </Typography>

                                                        </Tooltip>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', pb: 2 }}>
                                                            {program.code}
                                                        </Typography>
                                                    </CardContent>
                                                    <CardActions
                                                        sx={{
                                                            p: 0,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            flexWrap: 'nowrap',
                                                            width: '100%',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {/* Left Side: Status Label */}
                                                        {program.isUndergrad && (
                                                            <Tooltip title="UNDERGRAD">
                                                                <Chip
                                                                    label="UNDERGRAD"
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: '#E5F8FE',
                                                                        color: '#26C6F9',
                                                                        fontSize: '10px',
                                                                        flexShrink: 1,
                                                                        maxWidth: '60%',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        {/* Right Side: Edit and Delete Buttons */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                flexShrink: 0,
                                                                ml: 'auto'
                                                            }}
                                                        >
                                                            <IconButton
                                                                aria-label="edit"
                                                                sx={{
                                                                    backgroundColor: 'rgba(249, 249, 249, 1)',
                                                                    borderRadius: '5px',
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(230, 230, 230, 1)',
                                                                    },
                                                                }}
                                                                onClick={() => handleEdit(program)}
                                                            >
                                                                <ModeEditIcon fontSize="small" />
                                                            </IconButton>

                                                            <IconButton
                                                                aria-label="delete"
                                                                sx={{
                                                                    backgroundColor: 'rgba(249, 249, 249, 1)',
                                                                    borderRadius: '5px',
                                                                    ml: 1,
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(230, 230, 230, 1)',
                                                                    },
                                                                }}
                                                                onClick={() => handleDelete(program)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </CardActions>
                                                </Box>
                                            </Card>

                                        </Grid>
                                    ))
                                )}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
            >
                {currentDrawableState === 'create' && (
                    <CreateProgramDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                        bannerFileInputRef={bannerFileInputRef}
                        handleUploadBannerClick={handleUploadBannerClick}
                        handleBannerFileChange={handleBannerFileChange}
                        selectedBannerImage={selectedBannerImage}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditProgramDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        fileInputRef={fileInputRef}
                        handleUploadClick={handleUploadClick}
                        handleFileChange={handleFileChange}
                        selectedImage={selectedImage}
                        bannerFileInputRef={bannerFileInputRef}
                        handleUploadBannerClick={handleUploadBannerClick}
                        handleBannerFileChange={handleBannerFileChange}
                        selectedBannerImage={selectedBannerImage}
                        isFormModified={isFormModified}
                    />
                )}
            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Program"
                    itemToDelete={selectedProgramTitle}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default ProgramsPage;
